import { AxiosError } from "axios";
import { HttpService } from "data/http-service";
import { translate } from "ui/i18n";
import { Snackbar } from "ui/pages/snackbar";
import { ActivitiesPageFilters } from "domain/entities/activities.entities";
import { Tutorial } from "domain/entities/tutorials.entities";
import { getActivitiesFilters } from "data/activities/get-activities-filters";
import {
  tutorialDtoSchema,
  tutorialsDtoSchema,
} from "data/tutorials/tutorials.dto.schemas";
import { TutorialsGateway } from "domain/tutorials/tutorials.gateway";

export class TutorialsHttpGateway implements TutorialsGateway {
  async getTutorials(
    filters: Partial<ActivitiesPageFilters> & Partial<{ search: string }>
  ): Promise<{
    tutorials: Tutorial[];
    totalCount: number;
    success: boolean;
  }> {
    return HttpService.get("tutorials", {
      params: getActivitiesFilters(filters),
    })
      .then((response) => {
        if (response.status === 200) {
          const parsedTutorials = tutorialsDtoSchema.safeParse(
            response.data.data
          );
          if (parsedTutorials.success) {
            return {
              tutorials: parsedTutorials.data,
              totalCount: response.data.total,
              success: true,
            };
          } else return { tutorials: [], totalCount: 0, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Tutorials.FindError"),
          });
          return {
            tutorials: [],
            totalCount: 0,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Tutorials.FindError"),
        });
        return { tutorials: [] as Tutorial[], totalCount: 0, success: false };
      });
  }

  async getTutorial(id: number): Promise<{
    tutorial: Tutorial | null;
    success: boolean;
  }> {
    return HttpService.get(`tutorials/${id}`)
      .then((response) => {
        if (response.status === 200) {
          const parsedTutorial = tutorialDtoSchema.safeParse(response.data);
          if (parsedTutorial.success) {
            return {
              tutorial: parsedTutorial.data,
              success: true,
            };
          } else return { tutorial: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Tutorial.FindError"),
          });
          return {
            tutorial: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Tutorial.FindError"),
        });
        return { tutorial: null as Tutorial, success: false };
      });
  }

  async getFavoritesTutorials(tutorialIds: number[]): Promise<{
    tutorials: Tutorial[];
    success: boolean;
  }> {
    const ids = tutorialIds.length > 0 ? tutorialIds : [-1];
    return HttpService.get("tutorials", { params: { "id[$in]": ids } })
      .then((response) => {
        if (response.status === 200) {
          const parsedTutorials = tutorialsDtoSchema.safeParse(
            response.data.data
          );
          if (parsedTutorials.success) {
            return {
              tutorials: parsedTutorials.data,
              success: true,
            };
          } else return { tutorials: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Tutorials.FindError"),
          });
          return {
            tutorials: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Tutorials.FindError"),
        });
        return { tutorials: [] as Tutorial[], success: false };
      });
  }

  async getTutorialByInnizRef(innizRef: number): Promise<{
    tutorial: Tutorial;
    success: boolean;
  }> {
    return HttpService.get("tutorials", { params: { innizRef } })
      .then((response) => {
        if (response.status === 200) {
          const parsedTutorials = tutorialsDtoSchema.safeParse(
            response.data.data
          );
          if (parsedTutorials.success) {
            return {
              tutorial: parsedTutorials.data[0],
              success: true,
            };
          } else return { tutorial: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Tutorials.FindError"),
          });
          return {
            tutorial: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Tutorials.FindError"),
        });
        return { tutorial: null as Tutorial, success: false };
      });
  }

  async getRandomTutorial(): Promise<{
    tutorial: Tutorial | null;
    success: boolean;
  }> {
    return HttpService.get("tutorials/random")
      .then((response) => {
        if (response.status === 200) {
          const parsedTutorial = tutorialDtoSchema.safeParse(response.data);
          if (parsedTutorial.success) {
            return {
              tutorial: parsedTutorial.data,
              success: true,
            };
          } else return { tutorial: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Tutorial.FindError"),
          });
          return {
            tutorial: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Tutorial.FindError"),
        });
        return { tutorial: null as Tutorial, success: false };
      });
  }
}
